// src/hooks/useAppContext.ts
import { useContext, useMemo } from 'react';
import { AppContext } from '../context/AppContext';

export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}

// Schema-driven configuration hook with proper max_suggestions mapping
export function useUIConfig() {
  const { state } = useAppContext();
  
  return useMemo(() => {
    const defaultConfig = {
      pageSize: 25,
      maxPageSize: 50,
      paginationDelta: 2,
      toastDuration: 5000,
      sidebarWidth: 320,
      sidebarCollapsedWidth: 84,
      sidebarIconSize: 20, // FIXED: Added missing sidebarIconSize
      mobileBreakpoint: 768,
      tabletBreakpoint: 1024,
      maxSuggestions: 500,
      debounceDelay: 150
    };

    if (!state.schema?.ui_config) {
      return defaultConfig;
    }

    const apiConfig = state.schema.ui_config;
    
    return {
      // API-DRIVEN: Use schema values with fallbacks
      pageSize: apiConfig.default_page_size || defaultConfig.pageSize,
      maxPageSize: apiConfig.max_results_per_page || defaultConfig.maxPageSize,
      maxFilters: apiConfig.max_filters || 20,
      maxFilterValues: apiConfig.max_filter_values || 50,
      maxKeywordsPerField: apiConfig.max_keywords_per_field || 10,
      maxDropdownOptions: apiConfig.max_dropdown_options || 50000,
      maxDisplayedOptions: apiConfig.max_displayed_options || 1000,
      // FIXED: Map max_suggestions from API config
      maxSuggestions: apiConfig.max_suggestions || defaultConfig.maxSuggestions,
      
      // SIDEBAR: Use API sidebar config
      sidebarWidth: apiConfig.sidebar?.expanded_width || defaultConfig.sidebarWidth,
      sidebarCollapsedWidth: apiConfig.sidebar?.collapsed_width || defaultConfig.sidebarCollapsedWidth,
      sidebarIconSize: apiConfig.sidebar?.icon_size || defaultConfig.sidebarIconSize, // FIXED: Added missing field
      
      // DEFAULTS: Keep reasonable defaults for non-configurable items
      paginationDelta: defaultConfig.paginationDelta,
      toastDuration: defaultConfig.toastDuration,
      mobileBreakpoint: defaultConfig.mobileBreakpoint,
      tabletBreakpoint: defaultConfig.tabletBreakpoint,
      debounceDelay: defaultConfig.debounceDelay
    };
  }, [state.schema]);
}